import type { Destructor } from "@suse-o11y/destructor";
import { type OnResizeOptions, onResize } from "@suse-o11y/on-resize";
import { useLatest } from "@suse-o11y/use-latest";
import { useLayoutEffect } from "react";

export interface UseOnResizeOptions<TNode> extends Omit<OnResizeOptions<TNode>, "node"> {
	node: null | undefined | TNode;

	/**
	 * @default true
	 */
	enabled?: boolean;
}

export function useOnResize<TNode extends Element>(
	handler: (node: TNode, entry: ResizeObserverEntry) => void | Destructor,
	{ node, enabled = true, debounce }: UseOnResizeOptions<TNode>,
): void {
	const stableHandler = useLatest(handler);
	const enabledNode = enabled ? node : null;

	useLayoutEffect(() => {
		if (enabledNode == null) {
			return;
		}

		return onResize((...args) => stableHandler()(...args), { node: enabledNode, debounce });
	}, [enabledNode, stableHandler, debounce]);
}
